import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CustomNumberFormatPipe, ThemeService } from '@wind-log_library/fe-component-library';
import { TranslateService } from '@ngx-translate/core';
import { DurationPipe } from 'app/shared/pipes/duration/duration.pipe';
import Highcharts from 'highcharts';
import { DEFAULT_CHART_OPTIONS } from 'app/shared/helper/highchart-options';
import { isNil } from 'lodash';
import { DecimalPipe } from '@angular/common';

export interface IAnalyticsPieChartDropdownOptions {
  displayName: string;
  objectKey: string;
  objectSign?: string;
  isDuration?: boolean;
}

@Component({
    selector: 'app-kpi-analytics-pie-chart',
    templateUrl: './kpi-analytics-pie-chart.component.html',
    styleUrls: ['./kpi-analytics-pie-chart.component.scss'],
    standalone: false
})
export class KpiAnalyticsPieChartComponent implements OnInit, OnChanges {

  @Input() dropdownOptions: IAnalyticsPieChartDropdownOptions[] = [];
  @Input() mainAnalyticsData: any[] = null;
  @Input() preselectedIndex = 0;
  @Input() dataDisplayName  = 'KPI.COMMON.PLANNING';
  @Input() showKpiSelected = true;
  @Input() planningModuleView = '';
  @Input() showDetailedTooltip = false;
  @Input() handleSelectChangesOutside = false;
  @Input() dataUnit = null;
  @Input() showExportButton = false;
  @Output() selectedDropdownOption = new EventEmitter<number>(); 

  @ViewChild('contentWrapper', {static: false}) contentWrapper: ElementRef;
  @ViewChild('rightSide', {static: false}) rightSide: ElementRef;

  public kpiSelected: IAnalyticsPieChartDropdownOptions = null;
  public kpiDropdownOpen: boolean;
  public chartDatasets = [];
  public chartContainerBaseSize = 220;
  public chartContainerSize = this.chartContainerBaseSize;
  public chartPercentagesSum = 0;
  public checkedKpis = [];
  public isFullScreenMode: boolean;
  public allUnselected: boolean;
  public showInfoPopup = false;
  public popupText = '';
  public popupLeft = -1;
  public popupTop = -1;
  public dropdownOptionsLabels: string[];
  public labelSelectedOption:string;
  public id = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

  public chartOptions: Highcharts.Options;
  public highcharts = Highcharts;
  public chartRef: Highcharts.Chart | null = null;

  constructor(private themeService: ThemeService,
              private durationPipe: DurationPipe,
              private translate: TranslateService,
              private customNumberFormat: CustomNumberFormatPipe,
              private decimalPipe: DecimalPipe) {
            }

  ngOnInit() {
    this.kpiSelected = this.dropdownOptions[this.preselectedIndex];
    this.dropdownOptionsLabels = this.dropdownOptions.map(i => i.displayName);
    this.labelSelectedOption = this.dropdownOptionsLabels[this.preselectedIndex];
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.mainAnalyticsData && changes.mainAnalyticsData.currentValue) {
      this.checkedKpis = changes.mainAnalyticsData.currentValue.map(() => true);
      this.kpiSelected = this.dropdownOptions && this.dropdownOptions[this.preselectedIndex];
      this.labelSelectedOption = this.dropdownOptionsLabels && this.dropdownOptionsLabels[this.preselectedIndex];
      this.setChartData();
    }
  }

  public closeKpiDropdown(): void {
    this.kpiDropdownOpen = !this.kpiDropdownOpen;
  }

  public toggleKpi(index): void {
    if(this.handleSelectChangesOutside){
      this.selectedDropdownOption.emit(index);
    }else{
      this.kpiSelected = this.dropdownOptions[index];
      this.setChartData();
    }
  }

  public onCheckedChanges(): void {
    this.allUnselected = this.checkedKpis.every(i => !i);
    this.setChartData();
  }

  public fullScreenChanges(state: boolean): void {
    this.isFullScreenMode = state;
    this.setChartHeight();
  }

  public showPopup(event, i) {
    if (!this.showDetailedTooltip) {
      return;
    }
    this.popupLeft = event.clientX + 10 ;
    this.popupTop  = event.clientY + 10;
    this.showInfoPopup = true;
    this.popupText = this.mainAnalyticsData[i].description;
  }

  public hidePopup() {
    this.showInfoPopup = false;
  }

  public chartCallback: Highcharts.ChartCallbackFunction = (chart): void => {
    this.chartRef = chart;
  };

  private getValue = (value): string => {
    if (isNil(value)) return '-';
    return this.customNumberFormat.transform(this.decimalPipe.transform(value, '1.2-2')) + '%';
  }

  private getDescription = (index: number): string => {
    if (isNil(index)) return '-';
    return this.mainAnalyticsData[index].name;
  }

  private setChartOptions(): void {
    let data = [];
    const hasData = this.chartDatasets.length > 0 && this.chartDatasets[0].data.length > 0 && this.chartDatasets[0].data.some(v => v !== 0);
    const getValue = this.getValue;
    const getDescription = this.getDescription;
    if (hasData) {
      data = this.chartDatasets[0].data.map((a, i) => ({
        name: '',
        y: this.chartDatasets[0].data[i],
        color: this.chartDatasets[0].backgroundColor[i]
      }));
    }
    this.chartOptions = Highcharts.merge(DEFAULT_CHART_OPTIONS, {
      chart: {
        type: 'pie',
        height: hasData ? this.chartContainerSize : 1,
      },
      plotOptions: {
        series: {
          animation: { duration: 0 }
        }
      },
      tooltip: {
        enabled: true,
        outside: true,
        followPointer: true,
        hideDelay: 0,
        animation: { duration: 0 },
        useHTML: true,
        formatter: function() {
          return `
            <div class="font-weight-bold" style="font-size: 16px; font-weight: 600;">${this.point.name}</div>
            <div style="font-size: 12px">
              <span style="display: inline-block; width: 10px; height: 10px; background-color: ${this.point.color}"></span>
              ${getValue(this.point.percentage)}<br/>
            </div>
            <span style="font-size: 12px;"><b>${getDescription(this.point.index)}</b></span>
          `;
        }
      },
      series: [{
        type: 'pie',
        name: '',
        dataLabels: {
          enabled: true,
          distance: 5,
          formatter: function () {
            if (this.y === 0) return null;
            return this.point.name;
          }
        },
        states: {
          hover: { enabled: false },
          inactive: { enabled: false }
        },
        data: data
      }]
    })
  }

  private setChartHeight(): void {
    if (!this.rightSide || !this.contentWrapper) {
      return;
    }
    const rightSide = this.rightSide.nativeElement.offsetWidth;
    setTimeout(() => {
      const availableSpace = this.contentWrapper.nativeElement.offsetWidth - rightSide > 350 ? 350 : this.contentWrapper.nativeElement.offsetWidth - rightSide;
      this.chartContainerSize = availableSpace > this.chartContainerBaseSize ?
        (availableSpace > this.contentWrapper.nativeElement.offsetHeight ?
          this.contentWrapper.nativeElement.offsetHeight : availableSpace) : this.chartContainerBaseSize;
    }, 0);
  }


  private setChartData(): void {
    this.mainAnalyticsData = [...this.mainAnalyticsData].sort((a, b) => b[this.kpiSelected.objectKey] - a[this.kpiSelected.objectKey]);
    const sum = this.mainAnalyticsData.reduce((a, b, index: number) => this.checkedKpis[index] ? a + b[this.kpiSelected.objectKey] : a, 0);
    this.chartDatasets = [this.mainAnalyticsData.reduce((acc, data, index: number) => {
      const dataValue = this.checkedKpis[index] ?
        [data[this.kpiSelected.objectKey] > 0 ? (data[this.kpiSelected.objectKey] / sum * 100) : 0] : [0];
        return {
          data: [...acc.data, ...dataValue],
          backgroundColor: [...acc.backgroundColor, data.color],
          borderColor: [...acc.borderColor, data.color],
          borderWidth: 0,
          pointBackgroundColor: [...acc.pointBackgroundColor, data.color],
          pointBorderColor: [...acc.pointBorderColor, data.color],
          pointHoverBackgroundColor: [...acc.pointHoverBackgroundColor, data.color],
          pointHoverBorderColor: [...acc.pointHoverBorderColor, data.color]
        };
    }, {
      data: [],
      backgroundColor: [],
      borderColor: [],
      pointBackgroundColor: [],
      pointBorderColor: [],
      pointHoverBackgroundColor: [],
      pointHoverBorderColor: []
    })];
    this.chartPercentagesSum = sum;
    this.setChartOptions();
  }

  public changeOption({index}) {
    this.toggleKpi(index);
    this.labelSelectedOption = this.dropdownOptionsLabels[index];
  }


  public prepareDataToExport = () => {
    const columnHeaders = {
      label: this.translate.instant(this.dataDisplayName),
      value: this.translate.instant(this.kpiSelected?.displayName) + (this.kpiSelected?.objectSign ?` (${this.kpiSelected?.objectSign})` : ''),
      percentage: this.translate.instant('COMMON.PERCENTAGE') +" (%)"
    }
    
    return this.mainAnalyticsData.map((data, index) => {
      return {
        [columnHeaders.label]: data.name,
        [columnHeaders.value]: this.kpiSelected.isDuration ?  this.durationPipe.transform(data[this.kpiSelected.objectKey],true,this.dataUnit)  : data[this.kpiSelected.objectKey],
        [columnHeaders.percentage]: this.chartDatasets[0].data[index]
      };
    });
  }

}
